import { type AxiosError, type AxiosResponse } from 'axios';
import { call, put, takeLatest } from 'redux-saga/effects';

import { ActionKeys } from '~/actions/constants';
import { showDialog } from '~/actions/dialog.actions';
import * as filtersActions from '~/actions/filters.actions';
import { changeQueryString, updateQueryParam } from '~/actions/routing.actions';
import { type Filters } from '~/reducers/filters/filters.reducer';
import {
  getCurrentFilterGroups,
  getSelectedTab,
  getTempFilters,
} from '~/selectors/filters.selectors';
import {
  getNotEmptyOrdersFilters,
  getOrderFiltersValues,
  getQSSaved,
} from '~/selectors/router.selectors';
import * as filterService from '~/services/filters.service';
import { type JSONFilterGroup } from '~/types/FilterGroup';
import {
  cleanFilterCriteria,
  getTabName,
  parseFilterGroups,
  parseFilters,
} from '~/utils/filterGroups';

import { selectState } from '..';

function* saveFilterGroup(action: ReturnType<typeof filtersActions.saveFilterGroup>) {
  try {
    const payload = {
      filter_criteria: parseFilters(action.payload.filters),
      name: action.payload.filterGroupName,
    };

    const result: AxiosResponse<unknown> = yield call(filterService.createFilterGroup, payload);

    yield put(filtersActions.saveFilterGroupSuccess(result.data));
    yield put(updateQueryParam('saved', action.payload.filterGroupName));
  } catch (err) {
    let errorMessage = 'There is an error that requires investigation. Please contact support.';
    if (
      (err as AxiosError).response &&
      (err as AxiosError).response?.data &&
      (err as AxiosError).response?.data.detail
    )
      errorMessage = (err as AxiosError).response?.data.detail;

    const payload = {
      closeButtonText: 'Close',
      messages: [errorMessage],
    };
    yield put(filtersActions.saveFilterGroupError(errorMessage));
    yield put(showDialog(payload, 'error'));
  }
}

function* getFilterGroups() {
  try {
    const result: AxiosResponse<{ results: JSONFilterGroup[] }> = yield call(
      filterService.getFilterGroups,
    );

    const cleanedFilters = cleanFilterCriteria(result.data.results);
    yield put(filtersActions.getFilterGroupsSuccess(cleanedFilters));

    const savedTabName: string = yield selectState<string>(getQSSaved);
    const nonEmptyFilterValues: ReturnType<typeof getNotEmptyOrdersFilters> =
      yield selectState<ReturnType<typeof getNotEmptyOrdersFilters>>(getNotEmptyOrdersFilters);
    const filters: ReturnType<typeof getOrderFiltersValues> =
      yield selectState<ReturnType<typeof getOrderFiltersValues>>(getOrderFiltersValues);

    // TODO: This looks like a bug. getTabName expects filterGroups (parsedFilters) to be an array
    let parsedFilters = {};

    cleanedFilters.forEach((filterGroup) => {
      if (filterGroup.name === savedTabName)
        parsedFilters = parseFilterGroups(savedTabName, [filterGroup]);
    });

    const tabName = getTabName(nonEmptyFilterValues, filters, savedTabName, parsedFilters);

    yield put(filtersActions.changeTabShown(tabName));
  } catch (err) {
    // TODO: Make this better
    const errorMessage = 'There is an error that requires investigation. Please contact support.';
    yield put(filtersActions.getFilterGroupsError(errorMessage));
  }
}

function* deleteFilterGroup(action: ReturnType<typeof filtersActions.deleteFilterGroup>) {
  try {
    yield call(filterService.deleteFilterGroup, action.payload.filterGroupObjectID);
    yield call(getFilterGroups);
    yield put(filtersActions.deleteFilterGroupSuccess());
    yield put(changeQueryString({}));
    yield put(filtersActions.changeTabShown(''));
  } catch (err) {
    // TODO: Make this better
    const errorMessage = 'There is an error that requires investigation. Please contact support.';
    const payload = {
      closeButtonText: 'Close',
      messages: [errorMessage],
    };
    yield put(filtersActions.deleteFilterGroupError(errorMessage));
    yield put(showDialog(payload, 'error'));
  }
}

function* changeTabShown(action: ReturnType<typeof filtersActions.changeTabShown>) {
  const filterGroups: ReturnType<typeof getCurrentFilterGroups> =
    yield selectState<ReturnType<typeof getCurrentFilterGroups>>(getCurrentFilterGroups);
  const currentFilters: ReturnType<typeof getOrderFiltersValues> =
    yield selectState<ReturnType<typeof getOrderFiltersValues>>(getOrderFiltersValues);
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  let filters: any = {};

  const currentTabName: string = yield selectState<string>(getSelectedTab);

  if (currentTabName === action.payload.tabName) return;

  // Clear search term when switching tabs
  yield put(changeQueryString({}));

  if (!['', 'fulfilled', 'new', 'pending'].includes(action.payload.tabName)) {
    filters = parseFilterGroups(action.payload.tabName, filterGroups);
    filters['saved'] = action.payload.tabName;
  } else if (['fulfilled', 'pending'].includes(action.payload.tabName)) {
    filters['order_status'] = action.payload.tabName;
  } else {
    filters['order_status'] = undefined;
  }

  if (action.payload.tabName === 'new') {
    const tempFilters: Maybe<Filters> = yield selectState<Maybe<Filters>>(getTempFilters);
    yield put(changeQueryString(tempFilters!));
  } else {
    if (currentTabName === 'new') {
      // TODO: Change this. Ugly AF.
      if (Object.keys(filters).length <= 1 && !filters.order_status) {
        yield put(filtersActions.hideNewTab());
      } else {
        yield put(filtersActions.saveTempFilterGroup(currentFilters));
        yield put(filtersActions.showNewTab());
      }
    }

    yield put(changeQueryString(filters));
  }

  yield put(filtersActions.changeTabShownSuccess(action.payload.tabName));
}

export default [
  takeLatest(ActionKeys.FILTER_SAVE_FILTER_GROUP, saveFilterGroup),
  takeLatest(ActionKeys.FILTER_GET_FILTER_GROUPS, getFilterGroups),
  takeLatest(ActionKeys.FILTER_DELETE_FILTER_GROUP, deleteFilterGroup),
  takeLatest(ActionKeys.FILTER_CHANGE_TAB, changeTabShown),
];
